import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import {
  MdAdd,
  MdEdit,
  MdDelete,
  MdUpload,
  MdDownload,
  MdEmail,
  MdClose,
  MdSearch,
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdIndeterminateCheckBox,
  MdChevronLeft,
  MdChevronRight,
  MdPerson,
  MdContacts,
  MdSupervisorAccount
} from 'react-icons/md';
import MainLayout from '../components/Layout/MainLayout';
import contactService from '../services/contactService';

const LIMIT = 20;
const emptyForm = { name: '', mobile: '', mobile2: '', mobile3: '', email: '' };
const emptyEmailForm = { subject: '', message: '' };

// selected is a Map<_id string, {name, email}> so it works for contacts with no email
export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Map()); // _id -> {name, email}
  const [allMatchingSelected, setAllMatchingSelected] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [showEmailPanel, setShowEmailPanel] = useState(false);
  const [emailForm, setEmailForm] = useState(emptyEmailForm);
  const [sending, setSending] = useState(false);

  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fileInputRef = useRef(null);
  const searchTimerRef = useRef(null);
  const currentPage = useRef(1);
  const currentSearch = useRef('');
  const currentSource = useRef('all');

  const fetchContacts = useCallback(async (page = 1, searchTerm = '', source = 'all') => {
    currentPage.current = page;
    currentSearch.current = searchTerm;
    currentSource.current = source;
    setLoading(true);
    try {
      const data = await contactService.getContacts({ search: searchTerm, page, limit: LIMIT, source });
      setContacts(data.contacts || []);
      setPagination(data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchContacts(1, '', 'all'); }, [fetchContacts]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSelected(new Map());
      setAllMatchingSelected(false);
      fetchContacts(1, val, currentSource.current);
    }, 400);
  };

  const handlePageChange = (newPage) => {
    setSelected(new Map());
    setAllMatchingSelected(false);
    fetchContacts(newPage, currentSearch.current, currentSource.current);
  };

  const handleSourceFilter = (src) => {
    setSourceFilter(src);
    setSelected(new Map());
    setAllMatchingSelected(false);
    fetchContacts(1, currentSearch.current, src);
  };

  const clearSelection = () => {
    setSelected(new Map());
    setAllMatchingSelected(false);
  };

  // Selection helpers — keyed by _id string
  const pageIds = contacts.map(c => String(c._id));
  const allSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));
  const someSelected = pageIds.some(id => selected.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    const next = new Map(selected);
    if (allSelected) {
      pageIds.forEach(id => next.delete(id));
      setAllMatchingSelected(false);
    } else {
      contacts.forEach(c => next.set(String(c._id), { name: c.name, email: c.email || '' }));
    }
    setSelected(next);
  };

  const toggleSelect = (contact) => {
    const key = String(contact._id);
    const next = new Map(selected);
    if (next.has(key)) {
      next.delete(key);
      setAllMatchingSelected(false);
    } else {
      next.set(key, { name: contact.name, email: contact.email || '' });
    }
    setSelected(next);
  };

  // Select every contact matching the current search/filter, across all pages
  const handleSelectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const data = await contactService.getAllContactIds({ search: currentSearch.current, source: currentSource.current });
      const next = new Map();
      (data.contacts || []).forEach(c => next.set(String(c._id), { name: c.name, email: c.email || '' }));
      setSelected(next);
      setAllMatchingSelected(true);
    } catch {
      toast.error('Failed to select all matching contacts');
    } finally {
      setSelectingAll(false);
    }
  };

  // Add / Edit
  const openAdd = () => { setEditingContact(null); setForm(emptyForm); setShowAddForm(true); };
  const openEdit = (c) => {
    setEditingContact(c);
    setForm({ name: c.name, mobile: c.mobile || '', mobile2: c.mobile2 || '', mobile3: c.mobile3 || '', email: c.email || '' });
    setShowAddForm(true);
  };
  const closeForm = () => { setShowAddForm(false); setEditingContact(null); setForm(emptyForm); };
  const handleFormChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editingContact) {
        await contactService.updateContact(editingContact._id, form);
        toast.success('Contact updated');
      } else {
        await contactService.createContact(form);
        toast.success('Contact added');
      }
      closeForm();
      fetchContacts(currentPage.current, currentSearch.current, currentSource.current);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await contactService.deleteContact(id);
      toast.success('Contact deleted');
      fetchContacts(currentPage.current, currentSearch.current, currentSource.current);
    } catch { toast.error('Failed to delete contact'); }
  };

  // Import
  const handleImportClick = () => fileInputRef.current?.click();
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    try {
      const res = await contactService.importContacts(file);
      const { inserted, skipped, errors } = res.data;
      toast.success(`Imported: ${inserted}, Skipped: ${skipped}${errors?.length ? `, Errors: ${errors.length}` : ''}`);
      if (errors?.length) toast.warn(errors.slice(0, 3).join(' | '));
      fetchContacts(1, currentSearch.current, currentSource.current);
    } catch { toast.error('Failed to import contacts'); }
    finally { setImporting(false); }
  };

  // Export
  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await contactService.exportContacts();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts_${Date.now()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to export contacts'); }
    finally { setExporting(false); }
  };

  // Email
  const openEmailPanel = () => {
    if (selected.size === 0) { toast.info('Select at least one contact'); return; }
    setEmailForm(emptyEmailForm);
    setShowEmailPanel(true);
  };

  const handleSendEmails = async (e) => {
    e.preventDefault();
    if (!emailForm.subject.trim() || !emailForm.message.trim()) { toast.error('Subject and message are required'); return; }
    setSending(true);
    try {
      const recipients = [...selected.values()].filter(r => r.email);
      if (recipients.length === 0) { toast.error('None of the selected contacts have an email address'); setSending(false); return; }
      const res = await contactService.sendEmails(recipients, emailForm.subject, emailForm.message);
      const { sent, failed } = res.data;
      toast.success(`Sent: ${sent.length}${failed.length ? `, Failed: ${failed.length}` : ''}`);
      setShowEmailPanel(false);
      setSelected(new Map());
      setAllMatchingSelected(false);
    } catch { toast.error('Failed to send emails'); }
    finally { setSending(false); }
  };

  const btnBase = 'flex items-center gap-2 px-4 py-2 border border-border rounded-md text-sm font-medium bg-surface hover:bg-background transition-colors disabled:opacity-50 cursor-pointer';
  const btnPrimary = 'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-50';
  const inputBase = 'w-full px-3 py-2 border border-border rounded-md text-sm bg-background focus:outline-none focus:ring-2 focus:ring-green-700/30';

  return (
    <MainLayout>
      <div className="p-6 space-y-5">

        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-primary">Contact Management</h1>
            <p className="text-sm text-secondary mt-0.5">{pagination.total} total contacts</p>
            <p className="text-xs text-secondary mt-0.5">
              Import columns: Name, Email (optional), Mobile, Mobile 2, Mobile 3 (up to 3 numbers per contact)
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <input type="file" accept=".csv,.xlsx" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
            <button onClick={handleImportClick} disabled={importing} className={btnBase}>
              <MdUpload size={18} />{importing ? 'Importing...' : 'Import CSV/Excel'}
            </button>
            <button onClick={handleExport} disabled={exporting} className={btnBase}>
              <MdDownload size={18} />{exporting ? 'Exporting...' : 'Export Excel'}
            </button>
            <button onClick={openAdd} className={btnPrimary} style={{ backgroundColor: '#6B7F5C' }}>
              <MdAdd size={18} />Add Contact
            </button>
          </div>
        </div>

        {/* Search + Source Filter */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary" size={18} />
            <input
              type="text"
              placeholder="Search name, email, mobile..."
              value={search}
              onChange={handleSearchChange}
              className="w-full pl-9 pr-4 py-2 border border-border rounded-md text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-green-700/30"
            />
          </div>

          {/* Source filter tabs */}
          <div className="flex items-center gap-1 bg-background border border-border rounded-lg p-1">
            {[
              { key: 'all', label: 'All' },
              { key: 'candidate', label: 'Candidates', icon: <MdPerson size={14} /> },
              { key: 'hr', label: 'HR', icon: <MdSupervisorAccount size={14} /> },
              { key: 'contact', label: 'Contacts', icon: <MdContacts size={14} /> },
            ].map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => handleSourceFilter(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  sourceFilter === key
                    ? 'bg-surface shadow-sm text-primary border border-border'
                    : 'text-secondary hover:text-primary'
                }`}
              >
                {icon}{label}
              </button>
            ))}
          </div>
        </div>

        {/* Selection action bar */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center gap-4 bg-surface border border-border rounded-md px-4 py-2.5">
            <span className="text-sm font-medium text-primary">{selected.size} selected</span>
            <button onClick={openEmailPanel} className={btnPrimary + ' px-3 py-1.5'} style={{ backgroundColor: '#6B7F5C' }}>
              <MdEmail size={16} />Send Email
            </button>
            <button onClick={clearSelection} className="text-sm text-secondary hover:text-primary transition-colors">
              Clear selection
            </button>
            {allSelected && !allMatchingSelected && pagination.total > pageIds.length && (
              <span className="text-sm text-secondary">
                All {pageIds.length} contacts on this page are selected.{' '}
                <button
                  onClick={handleSelectAllMatching}
                  disabled={selectingAll}
                  className="font-medium underline hover:no-underline disabled:opacity-50"
                  style={{ color: '#6B7F5C' }}
                >
                  {selectingAll ? 'Selecting...' : `Select all ${pagination.total} contacts${sourceFilter !== 'all' || search ? ' matching this filter' : ''}`}
                </button>
              </span>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-surface border border-border rounded-lg overflow-hidden">
          {loading ? (
            <div className="py-16 text-center text-secondary text-sm">Loading...</div>
          ) : contacts.length === 0 ? (
            <div className="py-16 text-center text-secondary text-sm">
              {search || sourceFilter !== 'all'
                ? 'No contacts match your filters.'
                : 'No contacts yet. Add one or import a file.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-background">
                  <tr>
                    <th className="py-3 px-4 text-left w-10">
                      <button onClick={toggleSelectAll} className="text-secondary hover:text-primary">
                        {allSelected
                          ? <MdCheckBox size={20} style={{ color: '#6B7F5C' }} />
                          : someSelected
                            ? <MdIndeterminateCheckBox size={20} style={{ color: '#6B7F5C' }} />
                            : <MdCheckBoxOutlineBlank size={20} />
                        }
                      </button>
                    </th>
                    <th className="py-3 px-4 text-left font-semibold text-primary">Name</th>
                    <th className="py-3 px-4 text-left font-semibold text-primary">Mobile</th>
                    <th className="py-3 px-4 text-left font-semibold text-primary">Email</th>
                    <th className="py-3 px-4 text-left font-semibold text-primary">Source</th>
                    <th className="py-3 px-4 text-right font-semibold text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {contacts.map(contact => {
                    const key = String(contact._id);
                    const isSelected = selected.has(key);
                    return (
                      <tr
                        key={`${contact.source}_${key}`}
                        className={`transition-colors hover:bg-background ${isSelected ? 'bg-background' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <button onClick={() => toggleSelect(contact)} className="text-secondary hover:text-primary">
                            {isSelected
                              ? <MdCheckBox size={20} style={{ color: '#6B7F5C' }} />
                              : <MdCheckBoxOutlineBlank size={20} />
                            }
                          </button>
                        </td>
                        <td className="py-3 px-4 font-medium text-primary">{contact.name}</td>
                        <td className="py-3 px-4 text-secondary">
                          {[contact.mobile, contact.mobile2, contact.mobile3].filter(Boolean).map((m, i) => (
                            <div key={i}>{m}</div>
                          ))}
                          {![contact.mobile, contact.mobile2, contact.mobile3].some(Boolean) && '—'}
                        </td>
                        <td className="py-3 px-4 text-secondary">{contact.email || '—'}</td>
                        <td className="py-3 px-4">
                          {contact.source === 'candidate' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <MdPerson size={12} />Candidate
                            </span>
                          ) : contact.source === 'hr' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                              <MdSupervisorAccount size={12} />HR
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                              <MdContacts size={12} />Contact
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-2">
                            {contact.source === 'contact' ? (
                              <>
                                <button onClick={() => openEdit(contact)} className="p-1.5 rounded hover:bg-border/50 text-secondary hover:text-primary transition-colors" title="Edit">
                                  <MdEdit size={17} />
                                </button>
                                <button onClick={() => handleDelete(contact._id)} className="p-1.5 rounded hover:bg-red-50 text-secondary hover:text-red-600 transition-colors" title="Delete">
                                  <MdDelete size={17} />
                                </button>
                              </>
                            ) : contact.source === 'hr' ? (
                              <span className="text-xs text-secondary italic">Managed in Users</span>
                            ) : (
                              <span className="text-xs text-secondary italic">Managed in Candidates</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-secondary">
            <span>Page {pagination.page} of {pagination.totalPages} &nbsp;·&nbsp; {pagination.total} total</span>
            <div className="flex gap-1">
              <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page <= 1} className="p-1.5 rounded border border-border hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed">
                <MdChevronLeft size={18} />
              </button>
              <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages} className="p-1.5 rounded border border-border hover:bg-background disabled:opacity-40 disabled:cursor-not-allowed">
                <MdChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-primary">{editingContact ? 'Edit Contact' : 'Add Contact'}</h2>
              <button onClick={closeForm} className="p-1 text-secondary hover:text-primary"><MdClose size={22} /></button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Name *</label>
                <input type="text" name="name" value={form.name} onChange={handleFormChange} required placeholder="Full name" className={inputBase} />
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Mobile</label>
                <input type="text" name="mobile" value={form.mobile} onChange={handleFormChange} maxLength={20} placeholder="Mobile number" className={inputBase} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Mobile 2</label>
                  <input type="text" name="mobile2" value={form.mobile2} onChange={handleFormChange} maxLength={20} placeholder="Optional" className={inputBase} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-primary mb-1">Mobile 3</label>
                  <input type="text" name="mobile3" value={form.mobile3} onChange={handleFormChange} maxLength={20} placeholder="Optional" className={inputBase} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-primary mb-1">Email</label>
                <input type="email" name="email" value={form.email} onChange={handleFormChange} placeholder="email@example.com (optional)" className={inputBase} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeForm} className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-background transition-colors">Cancel</button>
                <button type="submit" disabled={saving} className={btnPrimary + ' px-5 py-2'} style={{ backgroundColor: '#6B7F5C' }}>
                  {saving ? 'Saving...' : editingContact ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Compose Panel */}
      {showEmailPanel && (
        <div className="fixed bottom-0 right-0 w-full max-w-md bg-surface border border-border border-b-0 rounded-tl-2xl shadow-2xl z-50 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-base font-semibold text-primary">Compose Email</h2>
              <p className="text-xs text-secondary mt-0.5">{selected.size} recipient{selected.size !== 1 ? 's' : ''} selected</p>
            </div>
            <button onClick={() => setShowEmailPanel(false)} className="p-1 text-secondary hover:text-primary"><MdClose size={20} /></button>
          </div>
          <form onSubmit={handleSendEmails} className="space-y-3">
            <input
              type="text"
              placeholder="Subject"
              value={emailForm.subject}
              onChange={e => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
              required
              className={inputBase}
            />
            <div>
              <textarea
                placeholder="Message body. Use {{name}} to personalize with each contact's name."
                value={emailForm.message}
                onChange={e => setEmailForm(prev => ({ ...prev, message: e.target.value }))}
                required
                rows={5}
                className={inputBase + ' resize-none'}
              />
              <p className="text-xs text-secondary mt-1">
                Tip: <code className="bg-background px-1 rounded">{'{{name}}'}</code> will be replaced with each contact's name.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setShowEmailPanel(false)} className="px-4 py-2 text-sm font-medium border border-border rounded-md hover:bg-background transition-colors">Cancel</button>
              <button type="submit" disabled={sending} className={btnPrimary + ' px-5 py-2'} style={{ backgroundColor: '#6B7F5C' }}>
                <MdEmail size={16} />{sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </form>
        </div>
      )}
    </MainLayout>
  );
}
